import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { TableCell, TableRow, Typography } from '@mui/material';

import {
    getBookingRequest,
    getBookingResponse,
    getBookingHistoryRequest,
    getBookingHistoryResponse,
} from '../../../store/Bookings/actions';
import messages from '../../../assets/locale/messages';
import { setPageMainTitle } from '../../../utils/Helpers';
import { ROUTE_PATHS } from '../../../utils/RoutesPaths';
import { ViewBgIcon } from '../../../utils/Icons';
import Breadcrumbs from '../../../components/BreadCrumbs';
import Table from '../../../components/Table';
import EmptyState from '../../../components/EmptyState';

const BookingHistory = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams();
    const lang = useSelector((state) => state.locale.lang);
    const isLoading = useSelector((state) => state.loader);
    const bookingData = useSelector((state) => state.bookings.bookingDetails);
    const bookingHistory = useSelector((state) => state.bookings.bookingHistory);
    const { bookings, shared } = messages[lang];

    useEffect(() => {
        return () => {
            dispatch(getBookingResponse({ bookingDetails: {} }));
            dispatch(getBookingHistoryResponse([]));
        };
    }, []);

    useEffect(() => {
        setPageMainTitle('bookingsManagement');
        dispatch(getBookingRequest(id));
    }, [lang]);

    useEffect(() => {
        if (bookingData?.multi_session?.id) {
            dispatch(
                getBookingHistoryRequest({
                    multi_session_id: bookingData.multi_session.id,
                    page: -1,
                })
            );
        }
    }, [bookingData?.multi_session?.id]);

    const handleViewBooking = (bookingId) => {
        history.push(ROUTE_PATHS.viewBooking.replace(':id', bookingId));
    };

    const getPackageName = (row) => {
        if (lang === 'en') {
            return row.multi_session_title_en || row.multi_session_title_ar || '-';
        }
        return row.multi_session_title_ar || row.multi_session_title_en || '-';
    };

    const renderTable = () => {
        if (!isLoading && bookingHistory?.length === 0) {
            return (
                <div className="bg-white rounded-4 d-flex justify-content-center align-items-center flex-column py-5">
                    <EmptyState
                        subTitle={shared.noData}
                        showActionButton={false}
                        NoDataListWithNoActionBtn={true}
                    />
                </div>
            );
        }

        return (
            <div className="bg-white rounded-4 my-3 bookings-table-list">
                <Table
                    headlines={[
                        shared.lists.appointmentId,
                        shared.lists.customerName,
                        bookings.history.packageName,
                        bookings.history.sessionNumberColumn,
                        bookings.history.bookingDate,
                        shared.lists.status,
                    ]}
                    hasActions={true}
                    rows={bookingHistory?.map((row, i) => (
                        <TableRow key={i}>
                            {/* Appointment ID */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                {row.appointment_number}
                            </TableCell>
                            {/* Customer Name */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                {row.customer?.name}
                            </TableCell>
                            {/* Package Name */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                {getPackageName(row)}
                            </TableCell>
                            {/* Session Number */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                {bookings.details.session} {row.session_number}
                            </TableCell>
                            {/* Booking Date */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                {row.booking_date || '-'}
                            </TableCell>
                            {/* Status */}
                            <TableCell className="font-regular text-dark-blue fsize-14 status-container">
                                <div className={`p-2 rounded-4 d-flex gap-1 justify-content-center ${row.status}-container`}>
                                    {bookings.status[row.status]}
                                </div>
                            </TableCell>
                            {/* View Action */}
                            <TableCell className="font-regular text-dark-blue fsize-14">
                                <ViewBgIcon
                                    className="pointer"
                                    onClick={() => handleViewBooking(row.id)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                />
            </div>
        );
    };

    return (
        <div className="bookings-page">
            <div className="mb-4">
                <Breadcrumbs
                    list={[
                        {
                            path: ROUTE_PATHS.bookings,
                            label: bookings.list.bookingList,
                        },
                        {
                            path: ROUTE_PATHS.viewBooking.replace(':id', id),
                            label:
                                bookings.details.booking +
                                (bookingData?.appointment_number
                                    ? ' (' + bookingData.appointment_number + ')'
                                    : ''),
                        },
                        {
                            path: '#',
                            label: bookings.history.bookingHistory,
                        },
                    ]}
                    className="mb-4 fsize-28 fweight-600"
                />
            </div>
            <div className="services-header mb-4">
                <Typography
                    variant="h4"
                    className="page-title">
                    {bookings.history.bookingHistory}
                </Typography>
            </div>
            {renderTable()}
        </div>
    );
};

export default BookingHistory;
